/**
 * SourceAttributionStrip — collapsible "rich location + sources" pill.
 *
 * Per user directive 2026-05-16: "I like the location section with Flag,
 * City, Country, (optional: specifity for the using feature: method used
 * for prayers, source for 3id Date etc, source for weather, .... NO
 * external links to keep users always inside the AWENI's Learning Castle)."
 *
 * Per Searchov 2026-05-16 deep-research (`services/logs/searchov/2026-05-16-
 * calendar-consolidation-and-source-attribution.md`):
 *   - Use `<button aria-expanded>` (NOT `<details>/<summary>` — inconsistent
 *     styling + awkward accessibility tree).
 *   - Use `<dl>` definition list inside the revealed region. `<dt>` carries
 *     the label ("Prayer method"), `<dd>` carries the value ("Umm al-Qura").
 *     Screen readers announce the relationship correctly.
 *   - Apple Weather attribution pattern: inline badge + expandable source
 *     list in-app. No external link required for native apps (App Store
 *     Review Guideline 5.1.1 + the user's "Learning Castle" constraint).
 *   - WCAG AA: `<button>` element (NOT `<span onclick>`), keyboard-focusable,
 *     `aria-controls` pointing to the region id.
 *
 * Visual shape: champagne pill with flag + city + country. Tap to expand a
 * source list (Hijri authority, Prayer method, Weather provider, etc.).
 * Each row may have a small in-app info popover (NOT a link) for context.
 *
 * Where it mounts:
 *   - YawmiModule.calendar header (Hijri authority row)
 *   - PrayerModule hero (Prayer method row)  [future]
 *   - WeatherModule (Weather source row)     [future]
 *
 * Boundary: this is a UI shell helper. NO Islamic content (verses, hadith,
 * Arabic religious text). Source NAMES only (e.g., "HCMM moonsighting",
 * "Open-Meteo") — per CLAUDE.md Yawmi vs Memoriz / Source attribution rules.
 */

import { getLocationLabel, type LocationLabel } from './LocationSource';

export interface AttributionRow {
  /** Stable id used for the dt + popover linkage (e.g., 'hijri', 'prayer'). */
  id: string;
  /** Localized label (e.g., "Hijri authority", "Prayer method"). */
  label: string;
  /** Localized value (e.g., "HCMM moonsighting", "Umm al-Qura"). */
  value: string;
  /** Optional short in-app info text. NEVER a URL — keeps users in the
   *  Castle. Rendered as a popover bound to a small ⓘ button. */
  info?: string;
}

export interface SourceAttributionStripDeps {
  /** Stable id for ARIA wiring (must be unique per page mount). */
  id: string;
  /** Per-surface rows. Hijri authority is auto-added when getLocationLabel()
   *  returns a known authority — callers pass only the extra rows. */
  rows: AttributionRow[];
  /** Optional override; defaults to getLocationLabel(). Useful for tests. */
  location?: LocationLabel;
  /** i18n strings the caller injects. Keeps the strip language-agnostic. */
  strings: {
    /** "Set your city for accurate features" — shown when no country is set. */
    setCity: string;
    /** Tooltip for the set-city prompt. */
    setCityTooltip: string;
    /** ARIA label for the expand toggle ("Show sources used for this page"). */
    expandAria: string;
    /** ARIA label for the collapse toggle ("Hide sources"). */
    collapseAria: string;
    /** ARIA label for the per-row info button ("More about this source"). */
    infoAria: string;
    /** "Hijri authority" — auto-row label. */
    hijriAuthorityLabel: string;
  };
}

/** Escape user-controllable strings before HTML interpolation. */
function escHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!));
}

/** Module-level singleton flag to inject the style sheet exactly once. */
const STYLE_FLAG = '__aweni_attribution_css__';

function injectStyles(): void {
  const w = window as unknown as Record<string, unknown>;
  if (w[STYLE_FLAG]) return;
  w[STYLE_FLAG] = true;
  const style = document.createElement('style');
  style.textContent = `
    .aweni-attr {
      display: inline-flex;
      flex-direction: column;
      gap: 4px;
      max-width: 100%;
      font-size: 12px;
      line-height: 1.3;
      color: var(--ln-text-secondary, #64748B);
    }
    .aweni-attr-toggle {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 4px 10px;
      background: var(--ln-bg-secondary, rgba(10, 10, 10, 0.04));
      border: 1px solid var(--ln-border, rgba(10, 10, 10, 0.08));
      border-radius: 999px;
      font: inherit;
      color: inherit;
      cursor: pointer;
      max-width: 100%;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      transition: background var(--motion-duration-fast) var(--motion-easing-default),
                  transform var(--motion-duration-fast) var(--motion-easing-default);
    }
    .aweni-attr-toggle:hover {
      background: var(--ln-bg-secondary, rgba(10, 10, 10, 0.08));
    }
    .aweni-attr-toggle:active { transform: scale(0.98); }
    .aweni-attr-toggle:focus-visible {
      outline: 2px solid var(--ln-accent, #0A0A0A);
      outline-offset: 2px;
    }
    .aweni-attr-flag { font-size: 13px; }
    .aweni-attr-city { font-weight: 600; color: var(--ln-text-primary, #1E293B); }
    .aweni-attr-sep { opacity: 0.4; }
    .aweni-attr-country { opacity: 0.85; }
    .aweni-attr-chevron {
      margin-inline-start: 2px;
      opacity: 0.6;
      transition: transform var(--motion-duration-base) var(--motion-easing-default);
      display: inline-block;
    }
    .aweni-attr-toggle[aria-expanded="true"] .aweni-attr-chevron {
      transform: rotate(180deg);
    }
    /* Prompt variant: "set your city" when nothing is on record. */
    .aweni-attr-toggle--prompt {
      background: rgba(10, 10, 10, 0.06);
      border-color: rgba(10, 10, 10, 0.18);
      color: var(--ln-accent, #0A0A0A);
    }
    .aweni-attr-toggle--prompt:hover {
      background: rgba(10, 10, 10, 0.10);
    }
    .aweni-attr-toggle--prompt .aweni-attr-city {
      font-weight: 600;
      color: var(--ln-accent, #0A0A0A);
    }
    /* The revealed source list — definition list per Searchov a11y guidance. */
    .aweni-attr-list {
      margin: 4px 0 0 0;
      padding: 8px 12px;
      background: var(--ln-bg-card, #FFFFFF);
      border: 1px solid var(--ln-border, rgba(10, 10, 10, 0.08));
      border-radius: 10px;
      display: grid;
      grid-template-columns: max-content 1fr;
      column-gap: 12px;
      row-gap: 6px;
      max-width: 320px;
    }
    .aweni-attr-list[hidden] { display: none !important; }
    .aweni-attr-list dt {
      font-size: 11px;
      font-weight: 600;
      color: var(--ln-text-secondary, #64748B);
      text-transform: uppercase;
      letter-spacing: 0.3px;
      align-self: center;
    }
    .aweni-attr-list dd {
      margin: 0;
      font-size: 12px;
      color: var(--ln-text-primary, #1E293B);
      display: inline-flex; align-items: center; gap: 6px;
    }
    .aweni-attr-info {
      width: 18px; height: 18px;
      display: inline-flex; align-items: center; justify-content: center;
      border-radius: 50%;
      background: transparent;
      border: 1px solid var(--ln-border, rgba(10, 10, 10, 0.16));
      color: var(--ln-text-secondary, #64748B);
      font: inherit;
      font-size: 11px;
      font-weight: 700;
      cursor: pointer;
      padding: 0;
      line-height: 1;
    }
    .aweni-attr-info:hover {
      background: var(--ln-bg-secondary, rgba(10, 10, 10, 0.06));
      color: var(--ln-text-primary, #1E293B);
    }
    .aweni-attr-info:focus-visible {
      outline: 2px solid var(--ln-accent, #0A0A0A);
      outline-offset: 1px;
    }
    /* Popover — in-app only, no external link. Anchored under the ⓘ button. */
    .aweni-attr-popover {
      position: relative;
      grid-column: 1 / -1;
      margin-top: 2px;
      padding: 8px 10px;
      background: var(--ln-bg-attention, #FFF8E7);
      border: 1px solid var(--ln-accent-ceremonial, #C8A96A);
      border-radius: 8px;
      font-size: 11px;
      line-height: 1.45;
      color: var(--ln-text-primary, #1E293B);
    }
    .aweni-attr-popover[hidden] { display: none !important; }
  `;
  document.head.appendChild(style);
}

export class SourceAttributionStrip {
  private deps: SourceAttributionStripDeps;
  private root: HTMLElement | null = null;
  private expanded = false;
  private openedInfoRow: string | null = null;
  private locationChangedListener: (() => void) | null = null;

  constructor(deps: SourceAttributionStripDeps) {
    this.deps = deps;
  }

  /** Render synchronously into `parent`. Returns the created root element so
   *  callers can position it (display:inline-flex by default). */
  mount(parent: HTMLElement): HTMLElement {
    injectStyles();
    const loc = this.deps.location ?? getLocationLabel();
    const root = document.createElement('aside');
    root.className = 'aweni-attr';
    root.innerHTML = this.markup(loc);
    parent.appendChild(root);
    this.root = root;
    this.wire();

    // Listen for location changes — when the user sets a city via the
    // priming flow, the strip should re-render with the new authority data.
    this.locationChangedListener = () => this.refresh();
    document.addEventListener('aweni:location-changed', this.locationChangedListener);

    return root;
  }

  unmount(): void {
    if (this.locationChangedListener) {
      document.removeEventListener('aweni:location-changed', this.locationChangedListener);
      this.locationChangedListener = null;
    }
    this.root?.remove();
    this.root = null;
  }

  /** Recompute markup from current LocationService state. Idempotent. */
  refresh(): void {
    if (!this.root) return;
    const loc = getLocationLabel();
    this.root.innerHTML = this.markup(loc);
    this.expanded = false;
    this.openedInfoRow = null;
    this.wire();
  }

  // -------- markup -------------------------------------------------------

  private markup(loc: LocationLabel): string {
    const listId = `aweni-attr-list-${escHtml(this.deps.id)}`;

    if (loc.needsLocation) {
      // No country on record → show a single-button prompt (no expansion).
      return `
        <button type="button"
                class="aweni-attr-toggle aweni-attr-toggle--prompt"
                data-action="set-location"
                title="${escHtml(this.deps.strings.setCityTooltip)}">
          <span class="aweni-attr-flag" aria-hidden="true">📍</span>
          <span class="aweni-attr-city">${escHtml(this.deps.strings.setCity)}</span>
          <span class="aweni-attr-chevron" aria-hidden="true">→</span>
        </button>
      `;
    }

    // Compose the row stack: Hijri authority (auto, when present) +
    // per-surface rows from deps. Callers don't pass the Hijri row — the
    // strip owns it because it's derived from the same LocationLabel.
    const allRows: AttributionRow[] = [];
    if (loc.auth) {
      allRows.push({
        id: 'hijri',
        label: this.deps.strings.hijriAuthorityLabel,
        value: loc.auth,
      });
    }
    allRows.push(...this.deps.rows);

    const rowsHtml = allRows
      .map((r) => {
        const popoverId = `aweni-attr-pop-${escHtml(this.deps.id)}-${escHtml(r.id)}`;
        const infoBtn = r.info
          ? `<button type="button"
                     class="aweni-attr-info"
                     data-row-info="${escHtml(r.id)}"
                     aria-controls="${popoverId}"
                     aria-expanded="false"
                     aria-label="${escHtml(this.deps.strings.infoAria)}">i</button>`
          : '';
        const popover = r.info
          ? `<div class="aweni-attr-popover" id="${popoverId}" hidden>${escHtml(r.info)}</div>`
          : '';
        return `
          <dt id="aweni-attr-dt-${escHtml(this.deps.id)}-${escHtml(r.id)}">${escHtml(r.label)}</dt>
          <dd aria-labelledby="aweni-attr-dt-${escHtml(this.deps.id)}-${escHtml(r.id)}">
            <span>${escHtml(r.value)}</span>${infoBtn}
          </dd>
          ${popover}
        `;
      })
      .join('');

    return `
      <button type="button"
              class="aweni-attr-toggle"
              data-action="toggle"
              aria-expanded="false"
              aria-controls="${listId}"
              aria-label="${escHtml(this.deps.strings.expandAria)}">
        ${loc.flag ? `<span class="aweni-attr-flag" aria-hidden="true">${loc.flag}</span>` : ''}
        ${loc.city ? `<span class="aweni-attr-city">${escHtml(loc.city)}</span>` : ''}
        ${loc.city && loc.cc ? `<span class="aweni-attr-sep" aria-hidden="true">·</span>` : ''}
        ${loc.cc ? `<span class="aweni-attr-country">${escHtml(loc.cc)}</span>` : ''}
        <span class="aweni-attr-chevron" aria-hidden="true">▾</span>
      </button>
      <dl id="${listId}" class="aweni-attr-list" hidden>${rowsHtml}</dl>
    `;
  }

  // -------- behavior -----------------------------------------------------

  private wire(): void {
    if (!this.root) return;

    // "Set your city" prompt → fire the cross-component priming request
    // (HomeModule listens; opens LocationPrimingCard). No external links.
    const setBtn = this.root.querySelector<HTMLButtonElement>('[data-action="set-location"]');
    if (setBtn) {
      setBtn.addEventListener('click', () => {
        try {
          document.dispatchEvent(new CustomEvent('aweni:request-location-priming', { detail: { source: 'attribution-strip' } }));
        } catch { /* */ }
      });
    }

    // Toggle expand
    const toggle = this.root.querySelector<HTMLButtonElement>('[data-action="toggle"]');
    const list = this.root.querySelector<HTMLElement>('.aweni-attr-list');
    if (toggle && list) {
      toggle.addEventListener('click', () => {
        this.expanded = !this.expanded;
        toggle.setAttribute('aria-expanded', String(this.expanded));
        toggle.setAttribute(
          'aria-label',
          this.expanded ? this.deps.strings.collapseAria : this.deps.strings.expandAria,
        );
        if (this.expanded) {
          list.removeAttribute('hidden');
        } else {
          list.setAttribute('hidden', '');
          // Also close any open popover when collapsing the parent list.
          this.closeAllPopovers();
        }
      });
    }

    // Per-row info popover (in-app, no external link)
    this.root.querySelectorAll<HTMLButtonElement>('[data-row-info]').forEach((btn) => {
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const id = btn.getAttribute('data-row-info');
        if (!id) return;
        const popoverId = btn.getAttribute('aria-controls');
        if (!popoverId) return;
        const popover = this.root?.querySelector<HTMLElement>(`#${CSS.escape(popoverId)}`);
        if (!popover) return;
        if (this.openedInfoRow === id) {
          popover.setAttribute('hidden', '');
          btn.setAttribute('aria-expanded', 'false');
          this.openedInfoRow = null;
        } else {
          this.closeAllPopovers();
          popover.removeAttribute('hidden');
          btn.setAttribute('aria-expanded', 'true');
          this.openedInfoRow = id;
        }
      });
    });
  }

  private closeAllPopovers(): void {
    if (!this.root) return;
    this.root.querySelectorAll<HTMLElement>('.aweni-attr-popover').forEach((p) => {
      p.setAttribute('hidden', '');
    });
    this.root.querySelectorAll<HTMLButtonElement>('[data-row-info]').forEach((b) => {
      b.setAttribute('aria-expanded', 'false');
    });
    this.openedInfoRow = null;
  }
}
